import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useState } from 'react';
import { answered } from 'src/components/property/UtilityPortalCredentials/asyncResult';

/**
 * Load a list once, with the cancelled-flag guard these pages kept re-implementing.
 *
 * Unwraps the store's `{ response }` envelope and forces an array, so a failed call renders an
 * empty table rather than crashing on `.map`. The cancelled flag is the reason this is shared: it
 * is easy to leave out, and without it a resolved fetch sets state on an unmounted admin tab.
 *
 * Takes the async model so a failure can be told from an empty result: `run()` swallows the error
 * and resolves `undefined`, so without this a dead endpoint renders as a healthy scraper that
 * simply has not published a run yet - on the page whose job is spotting exactly that.
 *
 * `errorMessage` may be a function of the model, so a page can surface the API's own detail (a 503 that says
 * credential storage is not configured reads very differently from a dead endpoint). `reload()` re-runs it.
 */
export const useAsyncList = function (asyncModel, run, errorMessage) {
  let deps = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];
  const _useState = useState({
      loading: true,
      error: '',
      rows: []
    }),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    attempt = _useState4[0],
    setAttempt = _useState4[1];
  const reload = useCallback(() => setAttempt(n => n + 1), []);
  useEffect(() => {
    let cancelled = false;
    const fail = () => {
      const message = typeof errorMessage === 'function' ? errorMessage(asyncModel) : errorMessage;
      if (!cancelled) setState({
        loading: false,
        error: message,
        rows: []
      });
    };
    setState(s => _objectSpread(_objectSpread({}, s), {}, {
      loading: true
    }));
    const load = async () => {
      try {
        var _resp$response;
        const resp = await run();
        const rows = (_resp$response = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response !== void 0 ? _resp$response : resp;
        // `|| []` here was the hole: a cancelled or superseded request resolves undefined WITHOUT
        // setting the model's error flag, so an absent payload became an empty list and rendered as
        // "the scraper has not published a run yet" - on the page whose job is spotting exactly
        // that kind of silence. An absent payload is a failure; only a real array is a result.
        if (!answered(resp) || asyncModel !== null && asyncModel !== void 0 && asyncModel.isError || !Array.isArray(rows)) {
          fail();
          return;
        }
        if (!cancelled) setState({
          loading: false,
          error: '',
          rows
        });
      } catch (ex) {
        fail();
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt]);
  return _objectSpread(_objectSpread({}, state), {}, {
    reload
  });
};